/* eslint-disable eqeqeq */
import { FC, useEffect, useRef, useState } from 'react'
import moment from 'moment'
import classNames from 'classnames'
import { Icon } from '@iconify/react'
import broomIcon from '@iconify/icons-mdi/broom'
import {
  ChevronRightIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/20/solid'
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { useAppSelector } from '../../store/hooks'
import { useDispatchGuides } from '../../hooks/useDispatchGuides'
import { getRoute } from '../../services/RouteServices'
import {
  saveDispatchs,
  manifestSaveOutcome,
  getManifestsByShipment,
} from '../../services/DispatchServices'
import { buildDispatchPayload } from '../../utils/buildDispatchPayload'
import { DispatchGuideDto, RouteInterface } from '../../interfaces'
import { GuidesDispatchTables } from './GuidesDispatchTables'
import { CreateDataDispatch } from './CreateDataDispatch'
import { FormSelect } from '../FormFields/FormSelect'
import { FormText } from '../FormFields/FormText'
import { FormDatePicker } from '../FormFields/FormDatePicker'
import { PrimaryButton, SecondaryButton } from '../Atoms/Buttons'
import { PaginationFooter } from '../Molecules/PaginationFooter'
import { ModalLoading } from '../Atoms/ModalLoading'
import { ConfirmModalStatus } from '../Preshipment/ModalSuccess'
import { MissingGuidesModal } from './MissingGuidesModal'
import { ManifestByShipmentDto } from '../../interfaces/Django/ManifestByShipmentDto'

/**
 * Flujo de creación de manifiesto de despacho.
 *
 * Vive acá y no en la página para poder montarse en dos hosts: la ruta
 * `/Dispatch/create` (flujo manual, sin props) y un modal dentro del detalle de
 * una carga masiva (con las guías de esa carga preseleccionadas). Una sola
 * implementación: lo que se arregla acá vale para los dos.
 *
 * GUARDADO EN DOS FASES
 * ---------------------
 *
 *   submittingRef = true
 *         ├── saveDispatchs (.NET) OK ──▶ PRINT ──▶ await onCreated(code)
 *         │                                             ├── resuelve ──┐
 *         │                                             └── RECHAZA ───┤
 *         └── saveDispatchs falla ──▶ ERROR ─────────────────────────────┤
 *                                                                        ▼
 *                                                    finally: submittingRef = false
 *
 * Las dos fases van dentro del mismo `try/finally`. `onCreated` lo implementa el
 * HOST, no este componente: si esa promesa rechaza y la bandera no se bajara,
 * Guardar quedaría muerto y el cierre bloqueado, con el manifiesto ya creado del
 * otro lado y sin más salida que recargar la página.
 *
 * Un rechazo de `onCreated` es fallo de MARCA, nunca de manifiesto: el
 * manifiesto ya existe y no se puede mostrar como error de creación.
 *
 * Este componente NO renderiza `DispatchPrintModal`: lo hace el host vía
 * `onRequestPrint`, después de cerrar. `DispatchPrintModal` es él mismo un
 * `ModalWidthFull` y escribe en `document.querySelector('iframe')`, así que
 * anidarlo dentro de otro modal apila diálogos y pelea por el mismo iframe.
 */
export interface DispatchCreationFlowProps {
  /** Números de guía a tildar tras la primera carga. Vacío = flujo manual. */
  preselectedGuides?: string[]
  /** true dentro de un modal: alturas acotadas, sin filtros de fecha. */
  embedded?: boolean
  /** Cerrar. Se ignora mientras hay un guardado en vuelo. */
  onClose?: () => void
  /**
   * Se llama tras un CreateManifest OK. El host marca donde tenga que marcar y
   * devuelve el resultado.
   *
   * En modo embebido (con `onClose`), tras esto el flujo SE CIERRA y el host se
   * queda con el aviso de éxito: el manifiesto ya existe y no hay nada más que
   * hacer en el formulario.
   */
  onCreated?: (manifest: {
    code: string
    id?: string | null
    /**
     * Guías tildadas que NO entraron al manifiesto por no declarar piezas.
     * Van al host porque es él quien muestra el éxito en modo embebido, y esas
     * guías no viajan: hay que decirlo ahí, no cuando el paquete no llegue.
     */
    rejected: DispatchGuideDto[]
  }) => Promise<{ ok: boolean; message?: string }>
  /** El usuario apretó "Imprimir" en el flujo standalone. Se llama a `onClose()` antes. */
  onRequestPrint?: (code: string) => void
  /**
   * NINGUNA guía de la carga quedó disponible para despacho.
   *
   * Lo resuelve el HOST, no este componente: un formulario de despacho sin una sola
   * guía de la carga no tiene nada que hacer, y dejarlo abierto invita a crear un
   * manifiesto que no es el que el operador vino a crear. El host cierra y explica.
   */
  onNoUsableGuides?: (info: {
    notInList: string[]
    withoutPieces: string[]
  }) => void
}

export const DispatchCreationFlow: FC<DispatchCreationFlowProps> = ({
  preselectedGuides,
  embedded = false,
  onClose,
  onCreated,
  onRequestPrint,
  onNoUsableGuides,
}) => {
  const user = useAppSelector((state) => state.user)

  const g = useDispatchGuides({
    accountCode: user?.client?.accountCode,
    accountId: user?.client?.id,
    preselectedGuides,
  })

  const [selectNext, setSelectNext] = useState(false)
  const [save, setSave] = useState(false)

  const [sale1Dispath, setSale1Dispath] = useState('')
  const [sale2Dispath, setSale2Dispath] = useState('')
  const [sale3Dispath, setSale3Dispath] = useState('')
  const [route, setRoute] = useState('')
  const [transport, setTransport] = useState('')
  const [driver, setDriver] = useState('')

  const [listRouteDispath, setListRouteDispath] = useState<RouteInterface[]>([])

  const [loading, setLoading] = useState(false)
  const [loadingStatus, setLoadingStatus] = useState(
    ConfirmModalStatus.PROCESSING,
  )
  const [loadingTitle, setLoadingTitle] = useState('Cargando...')
  const [manifestCode, setManifestCode] = useState('')

  /** Cubre las DOS fases del guardado. Ver el diagrama de arriba. */
  const submittingRef = useRef(false)

  // --- Rutas ---
  useEffect(() => {
    const fetchRoute = async () => {
      const res = await getRoute('' + user?.client?.BuCodeSource)
      if (!res?.didError)
        setListRouteDispath((res?.model as RouteInterface[]) ?? [])
    }
    fetchRoute()
  }, [user?.client?.BuCodeSource])

  // --- Carga de guías: errores a la modal ---
  useEffect(() => {
    if (!g.error) return
    setLoadingStatus(ConfirmModalStatus.ERROR)
    setLoadingTitle(`Error: ${g.error}`)
    setLoading(true)
  }, [g.error])

  useEffect(() => {
    if (g.loading && !submittingRef.current) {
      setLoadingStatus(ConfirmModalStatus.PROCESSING)
      setLoadingTitle('Cargando...')
      setLoading(true)
    } else if (!g.loading && !submittingRef.current) {
      setLoading((prev) =>
        loadingStatus === ConfirmModalStatus.PROCESSING ? false : prev,
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [g.loading])

  /**
   * D15 — cerrar al cambiar de cliente.
   *
   * El payload se arma con `user.client` leído AL GUARDAR. Si el operador cambia
   * de cliente con el modal abierto, las guías del cliente A terminan en un
   * manifiesto del cliente B: mercancía facturada al cliente equivocado. No se
   * delega la protección al backend.
   */
  const clientIdRef = useRef(user?.client?.id)
  useEffect(() => {
    if (!embedded) return
    if (clientIdRef.current === user?.client?.id) return
    clientIdRef.current = user?.client?.id
    if (submittingRef.current) return // el finally cierra al terminar
    g.clearSelection()
    onClose?.()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.client?.id, embedded])

  // --- Guardado ---
  useEffect(() => {
    if (!save) return
    if (submittingRef.current) return // doble click: la primera ya está en vuelo
    // Red de seguridad: desde el paso 2 se puede volver atrás y destildar todo, así
    // que el botón deshabilitado no alcanza como única barrera.
    if (g.preselectSummary && g.selectedFromBulk === 0) {
      setSave(false)
      setLoadingStatus(ConfirmModalStatus.ERROR)
      setLoadingTitle(
        'El manifiesto tiene que incluir al menos una guía de esta carga.',
      )
      setLoading(true)
      return
    }
    submittingRef.current = true

    const run = async () => {
      try {
        setLoadingStatus(ConfirmModalStatus.PROCESSING)
        setLoadingTitle('Creando manifiesto...')
        setLoading(true)

        const { payload, rejected } = buildDispatchPayload(g.selecteds, {
          routeID: route,
          transportID: transport,
          driverID: driver,
          seal1: sale1Dispath,
          seal2: sale2Dispath,
          seal3: sale3Dispath,
          routes: listRouteDispath,
          creationUser: user?.user?.login ?? 'ccw',
          accountID: user?.client?.id,
          accountSiteID: user?.client?.accountSiteID,
          buCodeSource: user?.client?.BuCodeSource ?? '',
        })

        const response = await saveDispatchs(payload)
        const outcome = manifestSaveOutcome(response)

        if (outcome === 'failed') {
          setLoadingStatus(ConfirmModalStatus.ERROR)
          setLoadingTitle(`Error: ${response?.errorMessage ?? ''}`)
          setLoading(true)
          return
        }

        if (outcome === 'unknown') {
          // El manifiesto PUEDE haberse creado. Ofrecer reintento acá produce
          // dos manifiestos para el mismo camión.
          setLoadingStatus(ConfirmModalStatus.WARNING)
          setLoadingTitle(
            'No pudimos confirmar si el manifiesto se creó.\nRevisá Consulta de manifiestos antes de volver a intentar.',
          )
          setLoading(true)
          return
        }

        const code = response!.model!.masterShipmentHeaderCode ?? ''
        const id = response!.model!.masterShipmentHeaderID
        setManifestCode(code)

        // Fase 2: el host marca. Un fallo acá NO es fallo de creación.
        let markMessage: string | undefined
        if (onCreated) {
          const marked = await onCreated({ code, id, rejected })
          if (!marked?.ok) {
            markMessage =
              marked?.message ?? 'No se pudo marcar la carga masiva.'
          }
        }

        g.clearSelection()
        setSelectNext(false)

        /**
         * ÉXITO EN MODO EMBEBIDO: este modal se cierra y el HOST se queda con el
         * resultado.
         *
         * El manifiesto ya existe: no hay nada más que hacer acá, y dejar el
         * formulario abierto detrás del aviso de éxito confunde (la lista de
         * atrás ya no trae esas guías, porque quedaron manifestadas). El host
         * muestra el número y la opción de imprimir, que sobreviven a que este
         * componente se desmonte.
         *
         * En ERROR el modal NO se cierra: el operador tiene que poder corregir y
         * reintentar, y esos caminos hacen `return` antes de llegar acá.
         */
        if (onClose) {
          onClose()
          return
        }

        // Standalone (`/Dispatch/create`): no hay host que tome la posta, así
        // que el aviso de éxito lo muestra este componente.
        const avisoPiezas = rejected.length
          ? `\n⚠ ${rejected.length} guía(s) sin piezas quedaron fuera: ${rejected
              .map((x) => x.numGuia)
              .join(', ')}`
          : ''
        setLoadingStatus(ConfirmModalStatus.PRINT)
        setLoadingTitle(
          `¡Se registro el despacho Nº ${code} con exito!${avisoPiezas}${
            markMessage ? `\n⚠ ${markMessage}` : ''
          }`,
        )
        setLoading(true)
        await g.refresh()
      } finally {
        // Pase lo que pase. Sin esto, un rechazo de onCreated deja Guardar
        // muerto y el cierre bloqueado para siempre.
        submittingRef.current = false
        setSave(false)
      }
    }

    run()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [save])

  const guardedClose = () => {
    if (submittingRef.current) return
    onClose?.()
  }

  // --- Guías de la carga que no están en la lista de despachables ---
  const [missingOpen, setMissingOpen] = useState(false)
  const [missingLoading, setMissingLoading] = useState(false)
  const [missingError, setMissingError] = useState<string | null>(null)
  const [missingResults, setMissingResults] = useState<ManifestByShipmentDto[]>(
    [],
  )
  /** La consulta se dispara UNA vez por preselección, no en cada render. */
  const askedMissingRef = useRef(false)

  /**
   * Averigua en qué manifiesto quedaron las guías de la carga que no aparecen en
   * `GetAllMissingCCW`, y lo muestra.
   *
   * Sin esto el operador solo ve un conteo ("3 no están disponibles") y no puede
   * hacer nada con él. Con el número de manifiesto puede decidir si sigue o para.
   *
   * Degrada: si la consulta falla (al escribir esto el endpoint todavía no estaba
   * desplegado) el modal igual se abre, con las guías y el botón de descarga. Lo
   * que NO puede pasar es que un aviso informativo tape el flujo de despacho.
   */
  useEffect(() => {
    const summaryNow = g.preselectSummary
    if (!summaryNow || askedMissingRef.current) return

    const missing = summaryNow.notInList
    // Todas las guías de la carga se pueden tildar y no falta ninguna: nada que avisar.
    if (missing.length === 0 && summaryNow.matched > 0) return
    askedMissingRef.current = true

    // NINGUNA guía de la carga sirve — sea porque no están en la lista o porque no
    // declaran piezas. Lo resuelve el host: cierra este modal y da la explicación.
    // Consultar acá duplicaría la llamada, y abrir el modal interno lo desmontaría a
    // mitad de camino junto con el flujo.
    if (summaryNow.matched === 0) {
      onNoUsableGuides?.({
        notInList: missing,
        withoutPieces: summaryNow.withoutPieces,
      })
      return
    }

    // Hay usables, pero faltan algunas: se consulta y se avisa sin cortar el flujo.
    if (missing.length === 0) return

    let cancelled = false
    setMissingOpen(true)
    setMissingLoading(true)
    getManifestsByShipment(missing)
      .then((res) => {
        if (cancelled) return
        if (res.didError) {
          setMissingError(res.errorMessage || 'consulta no disponible')
          setMissingResults([])
          return
        }
        setMissingError(null)
        setMissingResults(res.model ?? [])
      })
      .catch((e: any) => {
        if (!cancelled) {
          setMissingError(e?.message || 'consulta no disponible')
          setMissingResults([])
        }
      })
      .finally(() => {
        if (!cancelled) setMissingLoading(false)
      })

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [g.preselectSummary])

  const summary = g.preselectSummary

  /**
   * Con una carga de origen, el manifiesto tiene que incluir AL MENOS UNA guía de
   * esa carga. Si no, es otro manifiesto: uno de guías sueltas que quedaría marcado
   * como el manifiesto de esta carga masiva y taparía el botón para el que sí
   * corresponde. En el flujo manual (`/Dispatch/create`) no aplica: no hay carga.
   */
  const missingBulkGuide = !!summary && g.selectedFromBulk === 0
  // Solo cuenta guías DE LA CARGA: las sueltas que el operador sumó no salen de
  // este universo y no pueden restarse acá.
  const pendientes = summary
    ? Math.max(0, summary.requested - summary.unavailable - g.selectedFromBulk)
    : 0

  /**
   * Una sola fuente para el conteo, usada en la barra de selección y en el aviso
   * de cobertura. Si estuvieran escritos por separado terminarían diciendo
   * números distintos.
   *
   * Sin preselección (flujo manual de /Dispatch/create) no hay "carga" contra la
   * cual comparar: solo el total.
   */
  const resumenSeleccion = summary ? (
    <>
      <span className="font-bold">{g.selectedFromBulk}</span> de las{' '}
      {summary.requested} guías de esta carga
      {g.selectedExtra > 0 && (
        <>
          , más <span className="font-bold">{g.selectedExtra}</span> guía
          {g.selectedExtra === 1 ? '' : 's'} suelta
          {g.selectedExtra === 1 ? '' : 's'} que sumaste
        </>
      )}
    </>
  ) : (
    <>
      <span className="font-bold">{g.selecteds.length}</span> guía
      {g.selecteds.length === 1 ? '' : 's'} seleccionada
      {g.selecteds.length === 1 ? '' : 's'}
    </>
  )

  return (
    <div className="flex flex-1 flex-col">
      {!selectNext && (
        <div>
          <div className="flex flex-1 justify-between gap-2 md:gap-6 flex-col-reverse md:flex-row">
            <div className="flex flex-col sm:flex-row gap-4 w-full flex-1">
              <div className="w-full">
                <FormText
                  id="search"
                  name="search"
                  value={g.search}
                  label="Buscador"
                  labelClassname=" mb-1"
                  placeholder="Búsqueda rápida de guía..."
                  onChange={(e) => {
                    g.setSearch(e.target.value)
                    g.setPage(0)
                  }}
                />
              </div>
            </div>
            <div className="flex flex-col lg:flex-row items-end gap-2 w-full xl:w-3/5">
              <div className="flex flex-col sm:flex-row gap-4 w-full flex-1">
                <div className="w-full">
                  <FormDatePicker
                    id="shipments-date-from"
                    name="shipments-date-from"
                    label="Desde"
                    labelClassname="mb-1"
                    useRange={false}
                    maxDate={g.maxDate ?? undefined}
                    value={{ startDate: g.minDate, endDate: g.minDate }}
                    toggleClassName={(old) => classNames(old, 'text-main-600')}
                    onChange={(e) =>
                      g.setMinDate(
                        e?.startDate ? moment(e.startDate).toDate() : null,
                      )
                    }
                    configs={{ shortcuts: {} }}
                  />
                </div>
                <div className="w-full">
                  <FormDatePicker
                    id="shipments-date-to"
                    name="shipments-date-to"
                    label="Hasta"
                    labelClassname="mb-1"
                    useRange={false}
                    minDate={g.minDate ?? undefined}
                    value={{ startDate: g.maxDate, endDate: g.maxDate }}
                    toggleClassName={(old) => classNames(old, 'text-main-600')}
                    onChange={(e) =>
                      g.setMaxDate(
                        e?.startDate ? moment(e.startDate).toDate() : null,
                      )
                    }
                    configs={{ shortcuts: {} }}
                  />
                </div>
              </div>

              <div className="flex w-full sm:w-auto h-[42px]">
                <SecondaryButton
                  className="btn-limpiar mr-2"
                  title="Limpiar filtros"
                  onClick={g.clearFilters}
                >
                  <Icon icon={broomIcon} className="w-5 h-5" width="24" />
                </SecondaryButton>
                <PrimaryButton onClick={() => g.refresh()}>
                  Buscar <MagnifyingGlassIcon className="w-5 h-5 ml-2" />
                </PrimaryButton>
              </div>
            </div>
          </div>

          <div
            className={classNames(
              'flex justify-between items-center mb-1',
              embedded ? 'mt-6' : 'mt-12',
            )}
          >
            <div className="flex items-center text-xs">
              <span>Mostrar</span>
              <div className="inline mx-1">
                <FormSelect
                  id="registersNumber"
                  name="registersNumber"
                  label=""
                  selected={g.registersNumber}
                  options={[10, 25, 50, 100]}
                  className="!w-20 !h-8 !px-2"
                  optionString={(o) => o.toString()}
                  onSelectOption={(o) => g.setRegistersNumber(o)}
                />
              </div>
              <span>registros</span>
            </div>
          </div>

          {/* Restaurar la preselección. Va acá, FUERA de la barra de selección
              múltiple, justamente porque esa barra solo existe cuando hay algo
              tildado: si el operador limpia todo, desaparece — y ese es el caso
              en el que este botón hace falta. */}
          {g.restorableCount > 0 && (
            <div className="mb-2 flex flex-wrap items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
              <span>
                {g.selectedFromBulk === 0
                  ? `No tienes tildada ninguna de las ${summary?.matched} guías de esta carga.`
                  : `Te faltan ${g.restorableCount} guía${
                      g.restorableCount === 1 ? '' : 's'
                    } de esta carga por tildar.`}
              </span>
              <SecondaryButton
                className="!py-1 !text-xs sm:ml-auto"
                onClick={g.restorePreselection}
              >
                Seleccionar las guías de la carga
              </SecondaryButton>
            </div>
          )}
        </div>
      )}

      {!selectNext && (
        <div
          className={classNames(
            'flex flex-1 flex-col',
            embedded && 'max-h-[58vh] overflow-auto',
          )}
        >
          <GuidesDispatchTables
            sortBy={g.sortBy}
            dispatchGuides={g.guides}
            selectedKeys={g.selectedKeys}
            selectedAll={g.allFilteredSelected}
            onChangeSortBy={g.changeSort}
            onSelectGuide={(guide: DispatchGuideDto) => g.toggleGuide(guide)}
            onSelectAll={g.toggleAllFiltered}
            onSetGuidesSelected={g.setRangeSelected}
          />
          <PaginationFooter
            currentPage={g.page}
            itemsPerPage={g.registersNumber}
            totalItems={g.filteredCount}
            onPageChange={g.setPage}
          />
        </div>
      )}

      {/* Barra de selección múltiple, mismo patrón que /shipments/consult:
          sticky al fondo, reserva su propio espacio (no tapa la tabla ni la
          empuja al aparecer) y sigue visible al scrollear listas largas. */}
      {!selectNext && g.selecteds.length > 0 && (
        <div className="sticky bottom-0 z-20 -mx-4 mt-6 flex flex-col gap-3 border-t border-gray-200 bg-white px-4 py-3 shadow-[0_-2px_8px_rgba(0,0,0,0.04)] sm:flex-row sm:items-center">
          <span className="text-sm text-gray-900">
            {missingBulkGuide ? (
              // Sin esto el operador ve "Siguiente" gris y no sabe por qué: tiene
              // guías tildadas, solo que ninguna es de esta carga.
              <span className="font-semibold text-red-700">
                Tildá al menos una guía de esta carga para crear su manifiesto.
              </span>
            ) : (
              <>Vas a incluir {resumenSeleccion}.</>
            )}
          </span>

          <div className="flex flex-wrap items-center gap-2 sm:ml-auto">
            <SecondaryButton
              className="!py-1.5 !text-xs"
              onClick={g.clearSelection}
            >
              Limpiar
            </SecondaryButton>
            <PrimaryButton
              type="button"
              className="!py-1.5 !text-xs items-center gap-1.5"
              disabled={missingBulkGuide}
              title={
                missingBulkGuide
                  ? 'Tildá al menos una guía de esta carga para continuar'
                  : undefined
              }
              onClick={() => !missingBulkGuide && setSelectNext(true)}
            >
              Siguiente
              <ChevronRightIcon className="h-4 w-4" aria-hidden="true" />
            </PrimaryButton>
          </div>
        </div>
      )}

      {selectNext && (
        <div className="flex flex-1 flex-col">
          <CreateDataDispatch
            setSelectNext={setSelectNext}
            setSave={setSave}
            setSale1Dispath={setSale1Dispath}
            setSale2Dispath={setSale2Dispath}
            setSale3Dispath={setSale3Dispath}
            saveDispath={save}
            setRoute={setRoute}
            setTransport={setTransport}
            setDriver={setDriver}
          />

          {/* Aviso de cobertura, DEBAJO del panel de detalle de despacho: es lo
              último que el operador lee antes de Guardar, y es el número que
              decide si su mercancía viaja completa.

              `CreateDataDispatch` no se modifica: el aviso lo renderiza este
              componente, que es el que conoce la carga de origen. */}
          {summary && (
            <div className="mt-6 space-y-2 text-sm">
              {/* Amarillo `yellow-700` y no el gold de marca (#FBD872): ese gold
                  como TEXTO sobre blanco da 1.6:1 de contraste, ilegible. Y
                  `yellow-600` da 3.2:1, que a 14px tampoco alcanza el 4.5:1 que
                  pide WCAG AA. `yellow-700` es el más claro que sigue leyéndose
                  como amarillo y se puede leer.

                  Tampoco se usa `main-*`: es env-aware y se vuelve ROJO fuera de
                  producción, así que un resumen informativo parecía un error. */}
              <p className="flex items-start gap-2 font-bold text-yellow-700">
                <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0" />
                <span>Vas a incluir {resumenSeleccion}.</span>
              </p>

              {pendientes > 0 && (
                <p className="pl-7 font-semibold text-gray-600">
                  Las {pendientes} de esta carga que dejaste sin tildar las
                  podés despachar más adelante desde Creación de manifiesto.
                </p>
              )}

              {/* Dos causas distintas, dos avisos distintos. Juntas en un solo
                  conteo ("3 no están disponibles") el operador no sabía si
                  reclamar por unas guías perdidas o corregir el dato de otras. */}
              {summary.notInList.length > 0 && (
                <p className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 font-semibold text-amber-800">
                  <span>
                    {summary.notInList.length} guía
                    {summary.notInList.length === 1 ? '' : 's'} de esta carga no{' '}
                    {summary.notInList.length === 1 ? 'aparece' : 'aparecen'} en
                    la lista de despachables.
                  </span>
                  <button
                    type="button"
                    className="underline underline-offset-2 hover:no-underline"
                    onClick={() => setMissingOpen(true)}
                  >
                    Ver cuáles
                  </button>
                </p>
              )}

              {summary.withoutPieces.length > 0 && (
                <p className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 font-semibold text-amber-800">
                  {summary.withoutPieces.length} guía
                  {summary.withoutPieces.length === 1 ? '' : 's'} de esta carga
                  no{' '}
                  {summary.withoutPieces.length === 1 ? 'declara' : 'declaran'}{' '}
                  piezas, así que no se{' '}
                  {summary.withoutPieces.length === 1
                    ? 'preseleccionó'
                    : 'preseleccionaron'}
                  : {summary.withoutPieces.slice(0, 3).join(', ')}
                  {summary.withoutPieces.length > 3 ? ', …' : ''}.
                </p>
              )}
            </div>
          )}
        </div>
      )}

      <MissingGuidesModal
        open={missingOpen}
        onClose={() => setMissingOpen(false)}
        requested={summary?.notInList ?? []}
        results={missingResults}
        loading={missingLoading}
        error={missingError}
      />

      <ModalLoading
        open={loading}
        title={loadingTitle}
        status={loadingStatus}
        setOpen={setLoading}
        onPrint={() => {
          setLoading(false)
          if (onRequestPrint) {
            // Cerrar ANTES: DispatchPrintModal pelea por el primer iframe del
            // documento y no puede convivir con este modal abierto.
            guardedClose()
            onRequestPrint(manifestCode)
          }
        }}
      />
    </div>
  )
}
